import {
  readPreviousAnswer,
  isChoiceRows,
  type ChoiceValue,
  type ChoiceRow,
  type SavedChoiceRow,
  type AnswerJson,
} from "../answerJson";
import {
  decodeVisualGroups,
  type VisualGrouping,
  type RawVisualGrouping,
} from "../visualGroups";
import { defaultThemeV2 } from "../../theme/defaultThemeV2";
import type { QuestionCore } from "../contracts";
import { encodeResponses } from "../responseEncoding";
import {
  encodeRowAnswer,
  restoreRowLabels,
  rowAnswer,
  rowLabelDraft,
  updateRowLabel,
  validateRowLabels,
  type RowLabelDraft,
  restoreChoice,
} from "../specify/core";
import type { GridJsonResponse, SpecifiedRowAnswer } from "../specify/types";

export type GridMultiSelectRaw = RawVisualGrouping & {
  type: "grid_multi_select";
  content: string;
  style: "button" | "checkbox";
  options: {
    value: number;
    text: string;
    disabled: boolean;
    unique?: boolean;
    pop_up_text?: string | null;
  }[];
  dont_know_option?: { value: string; text: string } | null;
  rows: {
    value: string;
    text: string;
    image?: string | null;
    display_text: boolean;
    pop_up_text?: string | null;
  }[];
  previous_response?: (
    | string
    | (string | number)[]
    | SpecifiedRowAnswer<string | (string | number)[]>
  )[];
  image?: string | null;
  alt_text?: string | null;
  error_message?: string;
  previous_answer_json?: unknown;
};

export type GridMultiSelectDefinition = VisualGrouping & {
  prompt: string;
  options: readonly {
    id: number;
    label: string;
    disabled: boolean;
    exclusive: boolean;
    customPrompt?: string;
  }[];
  dontKnow?: { value: string; label: string };
  rows: readonly {
    id: string;
    label: string;
    image?: string;
    displayLabel: boolean;
    customPrompt?: string;
  }[];
  previousRowLabels?: Readonly<Record<string, string>>;
  previousResponses: readonly ChoiceRow[];
  image?: string;
  altText?: string;
  error?: string;
};

export type GridMultiSelectDraft = RowLabelDraft & {
  selectedByRow: Readonly<Record<string, readonly (number | string)[]>>;
  customTextByRow: Readonly<Record<string, string>>;
};
export type GridMultiSelectEvent =
  | { type: "toggle"; rowId: string; optionId: number | string }
  | {
      type: "set-custom-answer";
      rowId: string;
      optionId: number;
      value: string;
    }
  | { type: "set-row-label"; rowId: string; value: string };
export type GridMultiSelectResponse = {
  type: "grid_multi_select";
  value: string;
  responses: string;
  responseJson?: GridJsonResponse;
  answerJson: AnswerJson;
};

const ordered = (
  definition: GridMultiSelectDefinition,
  ids: readonly (number | string)[],
) => [
  ...definition.options
    .filter((option) => ids.includes(option.id))
    .map((option) => option.id),
  ...(definition.dontKnow && ids.includes(definition.dontKnow.value)
    ? [definition.dontKnow.value]
    : []),
];

const addSelection = (
  definition: GridMultiSelectDefinition,
  current: readonly (number | string)[],
  option:
    | GridMultiSelectDefinition["options"][number]
    | { id: string; exclusive: true },
) =>
  ordered(
    definition,
    option.exclusive
      ? [option.id]
      : [
          ...current.filter(
            (id) =>
              id !== definition.dontKnow?.value &&
              !definition.options.some(
                (item) => item.id === id && item.exclusive,
              ),
          ),
          option.id,
        ],
  );

const valuesForRow = (
  definition: GridMultiSelectDefinition,
  draft: GridMultiSelectDraft,
  rowId: string,
) =>
  draft.selectedByRow[rowId].map((id) => {
    if (definition.dontKnow && id === definition.dontKnow.value)
      return definition.dontKnow.value;
    const option = definition.options.find((item) => item.id === id)!;
    return option.customPrompt ? draft.customTextByRow[rowId] : option.id;
  });

export const gridMultiSelectCore: QuestionCore<
  GridMultiSelectRaw,
  GridMultiSelectDefinition,
  GridMultiSelectDraft,
  GridMultiSelectEvent,
  GridMultiSelectResponse
> = {
  decode: (raw) => {
    if (
      !Array.isArray(raw.options) ||
      raw.options.length === 0 ||
      !Array.isArray(raw.rows) ||
      raw.rows.length === 0
    ) {
      throw new Error("Grid multi-select requires rows and options.");
    }
    const optionIds = raw.options.map((item) => item.value);
    const rowIds = raw.rows.map((item) => item.value);
    if (
      optionIds.some((id) => !Number.isInteger(id)) ||
      new Set(optionIds).size !== optionIds.length ||
      rowIds.some((id) => typeof id !== "string" || !id) ||
      new Set(rowIds).size !== rowIds.length
    ) {
      throw new Error("Grid multi-select row and option IDs must be unique.");
    }
    if (
      raw.previous_answer_json === undefined &&
      raw.previous_response !== undefined &&
      (!Array.isArray(raw.previous_response) ||
        raw.previous_response.some((value) => {
          const answer = rowAnswer(value);
          return (
            typeof answer !== "string" &&
            (!Array.isArray(answer) ||
              answer.some(
                (item) => typeof item !== "string" && typeof item !== "number",
              ))
          );
        }))
    ) {
      throw new Error(
        "Grid multi-select previous response must contain a list of strings or row value lists.",
      );
    }
    const previous = readPreviousAnswer<SavedChoiceRow[]>(
      raw.previous_answer_json,
      raw.previous_response ?? [],
      isChoiceRows,
    );
    return {
      ...decodeVisualGroups(raw),
      prompt: raw.content,
      options: raw.options.map((option) => ({
        id: option.value,
        label: option.text,
        disabled: option.disabled,
        exclusive: option.unique ?? false,
        ...(option.pop_up_text ? { customPrompt: option.pop_up_text } : {}),
      })),
      rows: raw.rows.map((row) => ({
        id: row.value,
        label: row.text,
        displayLabel: row.display_text,
        ...(row.pop_up_text != null ? { customPrompt: row.pop_up_text } : {}),
        ...(row.image ? { image: row.image } : {}),
      })),
      previousResponses: previous.map((value) => rowAnswer(value)),
      ...(raw.rows.some((row) => row.pop_up_text != null)
        ? {
            previousRowLabels: restoreRowLabels(
              raw.rows.map((row) => ({
                id: row.value,
                customPrompt: row.pop_up_text ?? undefined,
              })),
              [previous],
            ),
          }
        : {}),
      ...(raw.dont_know_option
        ? {
            dontKnow: {
              value: raw.dont_know_option.value,
              label: raw.dont_know_option.text,
            },
          }
        : {}),
      ...(raw.image ? { image: raw.image } : {}),
      ...(raw.alt_text ? { altText: raw.alt_text } : {}),
      ...(raw.error_message ? { error: raw.error_message } : {}),
    };
  },
  initialize: (definition) => {
    const selectedByRow = Object.create(null) as Record<
      string,
      readonly (number | string)[]
    >;
    const customTextByRow = Object.create(null) as Record<string, string>;
    definition.rows.forEach((row, index) => {
      const raw = definition.previousResponses[index];
      if (!raw) return;
      const ids: (number | string)[] = [];
      const responseValues: ChoiceValue[] =
        typeof raw === "string"
          ? raw.split(",")
          : Array.isArray(raw)
            ? raw
            : [raw];
      for (const value of responseValues) {
        const responseValue = String(value);
        if (responseValue === definition.dontKnow?.value) {
          ids.splice(0, ids.length, responseValue);
          delete customTextByRow[row.id];
          break;
        }
        const restored = restoreChoice(definition.options, value);
        const option = definition.options.find(
          (item) => item.id === restored?.optionId,
        );
        if (!restored || !option || ids.includes(option.id)) continue;
        if (restored.customText !== undefined)
          customTextByRow[row.id] = restored.customText;
        if (option.customPrompt !== undefined && !customTextByRow[row.id])
          continue;
        if (option.exclusive) {
          ids.splice(0, ids.length, option.id);
          if (!option.customPrompt) delete customTextByRow[row.id];
          break;
        }
        ids.push(option.id);
      }
      selectedByRow[row.id] = ordered(definition, ids);
    });
    return {
      selectedByRow,
      customTextByRow,
      ...rowLabelDraft(definition.previousRowLabels),
    };
  },
  update: (definition, draft, event) => {
    const row = definition.rows.find((item) => item.id === event.rowId);
    if (event.type === "set-row-label") {
      return !row || row.customPrompt === undefined
        ? draft
        : {
            ...draft,
            specifiedRows: updateRowLabel(
              draft.specifiedRows,
              row.id,
              event.value,
            ),
          };
    }
    if (
      row &&
      definition.dontKnow &&
      event.optionId === definition.dontKnow.value
    ) {
      const current = draft.selectedByRow[row.id] ?? [];
      const customTextByRow = { ...draft.customTextByRow };
      delete customTextByRow[row.id];
      return {
        ...draft,
        selectedByRow: {
          ...draft.selectedByRow,
          [row.id]: current.includes(definition.dontKnow.value)
            ? []
            : addSelection(definition, current, {
                id: definition.dontKnow.value,
                exclusive: true,
              }),
        },
        customTextByRow,
      };
    }
    const option = definition.options.find(
      (item) => item.id === event.optionId,
    );
    if (!row || !option || option.disabled) return draft;
    const current = draft.selectedByRow[row.id] ?? [];
    if (event.type === "set-custom-answer") {
      if (option.customPrompt === undefined || !event.value.trim())
        return draft;
      return {
        ...draft,
        selectedByRow: {
          ...draft.selectedByRow,
          [row.id]: addSelection(
            definition,
            current.filter((id) => id !== option.id),
            option,
          ),
        },
        customTextByRow: { ...draft.customTextByRow, [row.id]: event.value },
      };
    }
    if (current.includes(option.id)) {
      const customTextByRow = { ...draft.customTextByRow };
      if (option.customPrompt) delete customTextByRow[row.id];
      return {
        ...draft,
        selectedByRow: {
          ...draft.selectedByRow,
          [row.id]: current.filter((id) => id !== option.id),
        },
        customTextByRow,
      };
    }
    if (option.customPrompt !== undefined) return draft;
    const selected = addSelection(definition, current, option);
    const customTextByRow = { ...draft.customTextByRow };
    if (
      !definition.options.some(
        (item) => item.customPrompt && selected.includes(item.id),
      )
    )
      delete customTextByRow[row.id];
    return {
      ...draft,
      selectedByRow: {
        ...draft.selectedByRow,
        [row.id]: selected,
      },
      customTextByRow,
    };
  },
  validate: (definition, draft, copy = defaultThemeV2.copy) => {
    const labels = validateRowLabels(
      definition.rows,
      draft.specifiedRows ?? {},
    );
    if (!labels.valid) return labels;
    for (const row of definition.rows) {
      const ids = draft.selectedByRow[row.id] ?? [];
      if (ids.length === 0 || new Set(ids).size !== ids.length) {
        return {
          valid: false,
          error: copy.validation.gridMultiSelect,
        };
      }
      if (
        ids.some((id) => {
          if (id === definition.dontKnow?.value) return false;
          const option = definition.options.find((item) => item.id === id);
          return (
            !option ||
            option.disabled ||
            (option.customPrompt && !draft.customTextByRow[row.id]?.trim())
          );
        })
      )
        return { valid: false, error: copy.validation.invalidRowSelection };
      if (
        ids.length > 1 &&
        (ids.includes(definition.dontKnow?.value ?? "") ||
          definition.options.some(
            (option) => option.exclusive && ids.includes(option.id),
          ))
      )
        return { valid: false, error: copy.validation.exclusiveOptions };
    }
    return { valid: true };
  },
  encode: (definition, draft) => {
    const validation = gridMultiSelectCore.validate(definition, draft);
    if (!validation.valid) throw new Error(validation.error);
    const rows = definition.rows.map((row) =>
      valuesForRow(definition, draft, row.id).join(","),
    );
    return {
      type: "grid_multi_select",
      value: rows.join("|"),
      responses: encodeResponses(rows),
      answerJson: definition.rows.map((row) =>
        encodeRowAnswer(
          row,
          draft.specifiedRows ?? {},
          draft.selectedByRow[row.id].map((id) => {
            if (typeof id === "string") return id;
            const option = definition.options.find((item) => item.id === id)!;
            return option.customPrompt !== undefined
              ? { custom: draft.customTextByRow[row.id] }
              : { option: id };
          }),
        ),
      ),
      ...(definition.rows.some((row) => row.customPrompt !== undefined)
        ? {
            responseJson: definition.rows.map((row) =>
              encodeRowAnswer(
                row,
                draft.specifiedRows!,
                valuesForRow(definition, draft, row.id).map(String),
              ),
            ),
          }
        : {}),
    };
  },
  projectTranscript: (definition, draft) => {
    const validation = gridMultiSelectCore.validate(definition, draft);
    if (!validation.valid) throw new Error(validation.error);
    return {
      kind: "grid_multi_select",
      question: definition.prompt,
      answer: definition.rows
        .map(
          (row) =>
            `${draft.specifiedRows?.[row.id] || row.label}: ${definition.options
              .filter((option) =>
                draft.selectedByRow[row.id]?.includes(option.id),
              )
              .map((option) =>
                option.customPrompt
                  ? draft.customTextByRow[row.id]
                  : option.label,
              )
              .concat(
                draft.selectedByRow[row.id]?.includes(
                  definition.dontKnow?.value ?? "",
                )
                  ? [definition.dontKnow!.label]
                  : [],
              )
              .join(", ")}`,
        )
        .join("\n"),
    };
  },
};
